import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, DollarSign, FileText, AlertTriangle, RefreshCcw, Briefcase, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/ui/Button';
import LocalizedNumberInput from '../components/ui/LocalizedNumberInput';
import { parseLocalizedNumber } from '../utils/numberFormat';
import { getLocalDateKey } from '../utils/localDateTime';
import {
  fetchClientTimelineBalanceSnapshot,
  getClientBalanceValue,
  updateClientBalance,
} from '../utils/clientBalances';

function ClientesMovimiento() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [cliente, setCliente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ tipo: 'Nota de Debito', monto: '', obs: '', fecha_flujo: getLocalDateKey(), proyectar_flujo: true });

  useEffect(() => {
    async function loadCliente() {
      try {
        setLoading(true);
        const { data, error } = await fetchClientTimelineBalanceSnapshot({
          clientId: id,
          organizationId: profile?.organization_id,
          supabaseClient: supabase,
        });
        if (error) throw error;
        setCliente(data);
      } catch (error) {
        toast.error('Error al cargar cliente');
        navigate('/sistema/clientes');
      } finally {
        setLoading(false);
      }
    }

    loadCliente();
  }, [id, navigate, profile?.organization_id]);

  const handleTipoChange = (e) => {
    const valor = e.target.value;
    if (valor === 'Cobro') {
      toast.loading('Redirigiendo a Tesoreria...');
      navigate('/sistema/tesoreria-cobranzas', { state: { clientId: id } });
    } else {
      setForm({ ...form, tipo: valor });
    }
  };

  const guardar = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const monto = parseLocalizedNumber(form.monto);
      if (!monto || monto <= 0) {
        toast.error('Ingresá un monto válido');
        return;
      }
      const impacto = (form.tipo === 'Nota de Debito' || form.tipo === 'Ajuste (+)') ? monto : -monto;
      const saldoActual = getClientBalanceValue(cliente);
      const nuevoSaldo = saldoActual + impacto;

      const { error: errorCli } = await updateClientBalance({
        clientId: id,
        nextBalance: nuevoSaldo,
        supabaseClient: supabase,
      });
      if (errorCli) throw errorCli;

      const { error: errorMov } = await supabase.from('client_movements').insert([{
        client_id: id,
        organization_id: profile.organization_id,
        type: form.tipo,
        amount: impacto,
        current_balance: nuevoSaldo,
        observations: form.obs
      }]);
      if (errorMov) throw errorMov;

      if (form.proyectar_flujo) {
        const proyTipo = impacto >= 0 ? 'ingreso' : 'egreso';
        const proyConcepto = `${form.tipo} cliente ${cliente?.full_name || ''}${form.obs ? ` - ${form.obs}` : ''}`.trim();
        const { error: proyError } = await supabase.from('proyecciones').insert([{
          organization_id: profile.organization_id,
          concepto: proyConcepto.toUpperCase(),
          monto: Math.abs(impacto),
          fecha: form.fecha_flujo || getLocalDateKey(),
          tipo: proyTipo,
          categoria: 'Cuenta Corriente',
          sitio_id: null,
        }]);
        if (proyError) {
          console.warn('No se pudo proyectar ajuste de cliente en flujo de fondos:', proyError);
          toast('El ajuste se guardo, pero no se pudo crear la proyeccion de flujo.');
        }
      }

      toast.success('Movimiento registrado con exito');
      navigate(`/sistema/clientes-detalle/${id}`);
    } catch (error) {
      toast.error('Error al guardar el movimiento');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin mb-4 text-[#51A7FD]" size={34} />
        <p className="font-black uppercase tracking-[0.25em] text-slate-400 text-[10px]">Cargando legajo...</p>
      </div>
    );
  }

  return (
    <div className="w-full px-3 py-3 sm:px-4 sm:py-4 lg:px-5 bg-[#F8FAFC] min-h-screen font-sans text-slate-700 animate-in fade-in duration-500">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-3 px-1">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/sistema/clientes-detalle/${id}`)}
            className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#51A7FD] shadow-sm transition-all"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <FileText size={18} className="text-[#3FF2AA]" />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl lg:text-2xl font-black text-slate-800 tracking-tight uppercase">Movimiento Manual</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Ajuste de cuenta corriente para {cliente?.full_name || 'cliente'}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5 mb-3">
        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Cliente</p>
            <Briefcase size={16} className="text-slate-300" />
          </div>
          <p className="mt-1 text-lg font-black text-slate-800 tracking-tight truncate">{cliente?.full_name || '-'}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Legajo activo</p>
        </div>
        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Saldo actual</p>
            <DollarSign size={16} className="text-slate-300" />
          </div>
          <p className="mt-1 text-lg font-black text-slate-800 tracking-tight">$ {getClientBalanceValue(cliente).toLocaleString('es-AR')}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Antes del ajuste</p>
        </div>
        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm sm:col-span-2 xl:col-span-1">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Operacion</p>
            <FileText size={16} className="text-slate-300" />
          </div>
          <p className="mt-1 text-lg font-black text-slate-800 tracking-tight">{form.tipo}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Impacto manual sobre la cuenta</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="bg-slate-50/50 px-4 py-3 sm:px-5 border-b border-slate-100 flex items-center gap-3">
          <FileText size={16} className="text-[#51A7FD]" />
          <h3 className="text-[11px] font-black uppercase tracking-widest text-slate-500">Carga de movimiento</h3>
        </div>

        <form onSubmit={guardar} className="p-4 sm:p-5 space-y-4">
          <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 flex gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center text-amber-600 shrink-0">
              <AlertTriangle size={18} />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase text-amber-800 tracking-widest mb-1">Aviso de impacto contable</p>
              <p className="text-[11px] font-medium text-amber-700 leading-relaxed">
                Si eliges <strong>Cobro</strong> te redirigimos a Tesoreria. Las notas de debito aumentan la deuda y las notas de credito la reducen.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1 block mb-1">Tipo de operacion</label>
              <select
                className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-[11px] font-black uppercase outline-none focus:border-[#51A7FD]"
                value={form.tipo}
                onChange={handleTipoChange}
              >
                <option value="Nota de Debito">Nota de Debito (Suma deuda)</option>
                <option value="Nota de Credito">Nota de Credito (Resta deuda)</option>
                <option value="Ajuste (+)">Ajuste manual (+)</option>
                <option value="Ajuste (-)">Ajuste manual (-)</option>
                <option value="Cobro">Registrar cobro (redirige a Tesoreria)</option>
              </select>
            </div>

            <div>
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1 block mb-1">Importe total</label>
              <div className="relative">
                <DollarSign className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                <LocalizedNumberInput
                  decimals={2}
                  selectOnFocus
                  className="w-full bg-slate-50 border border-slate-100 rounded-xl py-3 pl-10 pr-4 text-[11px] font-black text-slate-900 outline-none focus:border-[#51A7FD]"
                  placeholder="0,00"
                  value={form.monto}
                  onValueChange={(display) => setForm({ ...form, monto: display })}
                  required
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1 block mb-1">Fecha en flujo</label>
              <div className="relative">
                <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                <input
                  type="date"
                  className="w-full bg-slate-50 border border-slate-100 rounded-xl py-3 pl-10 pr-4 text-[11px] font-black text-slate-900 outline-none focus:border-[#51A7FD]"
                  value={form.fecha_flujo}
                  onChange={(e) => setForm({ ...form, fecha_flujo: e.target.value })}
                />
              </div>
            </div>

            <label className="flex items-center gap-3 bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 mt-0 md:mt-5 cursor-pointer">
              <input
                type="checkbox"
                checked={form.proyectar_flujo}
                onChange={(e) => setForm({ ...form, proyectar_flujo: e.target.checked })}
                className="h-4 w-4 accent-[#3FF2AA]"
              />
              <span className="text-[10px] font-black uppercase text-slate-500 tracking-widest">Ver en flujo de fondos</span>
            </label>
          </div>

          <div>
            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1 block mb-1">Observaciones</label>
            <textarea
              className="w-full bg-slate-50 border border-slate-100 rounded-xl p-4 text-[11px] font-bold text-slate-600 outline-none focus:border-[#51A7FD] h-28 resize-none"
              placeholder="Ej: Ajuste por error en carga anterior..."
              value={form.obs}
              onChange={(e) => setForm({ ...form, obs: e.target.value })}
            />
          </div>

          <Button
            type="submit"
            isLoading={saving}
            className="w-full h-12 bg-[#3FF2AA] text-[#0B2A36] rounded-2xl shadow-lg shadow-emerald-100 hover:scale-[1.01] transition-all active:scale-95 flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-[0.3em] disabled:opacity-50"
          >
            Confirmar movimiento
          </Button>
        </form>
      </div>
    </div>
  );
}

export default ClientesMovimiento;
